import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '@contexts/AuthContext';

// Tipos para o sistema de permissões (formato do permission-config.json, igual ao ERP)
interface PermissionConfig {
  roles: Record<string, {
    name: string;
    permissions: string[];
  }>;
  permissions: Record<string, unknown>;
}

interface PermissionResult {
  hasPermission: boolean;
  reason?: string;
}

interface UsePermissionsReturn {
  checkPermission: (permission: string) => PermissionResult;
  hasRole: (role: string) => boolean;
  userRoles: string[];
  isLoading: boolean;
  error: string | null;
}

// Cache (por módulo) da configuração de permissões
let permissionConfigCache: PermissionConfig | null = null;
let configLoadPromise: Promise<PermissionConfig> | null = null;

/** Carrega /security/permission-config.json uma única vez (requisições simultâneas compartilham a promise). */
const loadPermissionConfig = async (): Promise<PermissionConfig> => {
  if (permissionConfigCache) return permissionConfigCache;
  if (configLoadPromise) return configLoadPromise;

  configLoadPromise = (async () => {
    try {
      const response = await fetch('/security/permission-config.json', { cache: 'no-store' });
      if (!response.ok) {
        throw new Error(`Falha ao carregar configuração de permissões: ${response.status} ${response.statusText}`);
      }
      const config: PermissionConfig = await response.json();
      permissionConfigCache = config;
      return config;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Erro desconhecido';
      console.error('Erro ao carregar configuração de permissões:', errorMessage);
      throw err;
    } finally {
      configLoadPromise = null;
    }
  })();

  return configLoadPromise;
};

/**
 * Hook de autorização (mesma regra do ERPWebUI — ver CLAUDE.md, "Authorization"):
 *  1. atalho positivo por `user.permissions` (authService.mapRolesToPermissions, só admin);
 *  2. fonte autoritativa: `/security/permission-config.json` → roles[role].permissions.
 */
export const usePermissions = (): UsePermissionsReturn => {
  const { user } = useAuth();
  const [permissionConfig, setPermissionConfig] = useState<PermissionConfig | null>(permissionConfigCache);
  // Começa em loading quando ainda não há cache, para o PermissionGuard mostrar skeleton
  // em vez de uma negação falsa.
  const [isLoading, setIsLoading] = useState(!permissionConfigCache);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (permissionConfigCache) {
      setPermissionConfig(permissionConfigCache);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    loadPermissionConfig()
      .then((config) => {
        if (cancelled) return;
        setPermissionConfig(config);
        setIsLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Error loading permissions:', err);
        setError(err instanceof Error ? err.message : 'Erro desconhecido');
        setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const userRoles = useMemo<string[]>(() => {
    let roles: string[] = [];
    if (user?.roles && Array.isArray(user.roles) && user.roles.length > 0) {
      roles = user.roles
        .filter((role): role is string => typeof role === 'string' && role.trim().length > 0)
        .map((role) => role.trim());
    }
    if (roles.length === 0 && user?.role && typeof user.role === 'string' && user.role.trim().length > 0) {
      roles = [user.role.trim()];
    }
    return roles;
  }, [user]);

  /**
   * Verificar se usuário tem uma permissão específica
   */
  const checkPermission = useCallback((permission: string): PermissionResult => {
    if (!user) {
      return { hasPermission: false, reason: 'Usuário não autenticado' };
    }

    // PRIMEIRO: permissões diretas do usuário (authService) como atalho positivo
    if (user.permissions && Array.isArray(user.permissions) && user.permissions.length > 0) {
      if (user.permissions.includes('*')) return { hasPermission: true };
      if (user.permissions.includes(permission)) return { hasPermission: true };
      // Não encontrada → continua verificando pelas roles no config
    }

    // SECUNDÁRIO: configuração de permissões via roles
    if (permissionConfig) {
      for (const role of userRoles) {
        const roleConfig = permissionConfig.roles[role];
        if (!roleConfig) continue;
        if (roleConfig.permissions.includes('*')) return { hasPermission: true };
        if (roleConfig.permissions.includes(permission)) return { hasPermission: true };
      }
    } else {
      // Fallback: se a configuração não carregou mas o usuário é admin, libera
      const userRole = user.role || user.roles?.[0];
      if (userRole === 'admin' || userRole === 'administrator') {
        return { hasPermission: true };
      }
    }

    return {
      hasPermission: false,
      reason: userRoles.length > 0
        ? `Role(s) [${userRoles.join(', ')}] do not have permission '${permission}'`
        : 'User has no valid roles',
    };
  }, [user, userRoles, permissionConfig]);

  /**
   * Verificar se usuário tem uma role específica
   */
  const hasRole = useCallback((role: string): boolean => userRoles.includes(role), [userRoles]);

  return {
    checkPermission,
    hasRole,
    userRoles,
    isLoading,
    error,
  };
};
